import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { Avatar, Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { differenceInDays } from "date-fns";
import { blankIfPlaceholder } from "utils/textNormalize";
import { formatDisplayDate, parseToDate } from "utils/dateFormat";

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const formatClientDate = (value) => {
  if (!value) return null;
  return formatDisplayDate(value, null);
};

const getExpiryMeta = (value) => {
  const display = formatClientDate(value) || "Not provided";
  if (!formatClientDate(value)) {
    return { display: "Not provided", chip: null };
  }

  let days = null;
  const parsed = parseToDate(value);
  if (parsed) {
    days = differenceInDays(parsed, new Date());
  }

  if (days == null) return { display, chip: null };
  if (days < 0) {
    return { display, chip: { label: "Expired", color: "error" } };
  }
  if (days <= 90) {
    return {
      display,
      chip: { label: `Expires in ${days} day${days === 1 ? "" : "s"}`, color: "warning" },
    };
  }
  return { display, chip: null };
};

const CompactDetail = ({ label, value, chip }) => (
  <Box className="min-w-0">
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: 10, letterSpacing: 0.4, fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.25 }}>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ fontSize: 13 }}
        className="text-app-text break-words"
      >
        {value || "—"}
      </Typography>
      {chip && (
        <Chip
          size="small"
          label={chip.label}
          color={chip.color}
          variant="outlined"
          sx={{ height: 20, fontSize: 11 }}
        />
      )}
    </Stack>
  </Box>
);

const ClientSummaryCard = ({ data, clientList, onViewClient }) => {
  const clientId = data?.clientId ?? data?.id;
  if (!clientId) return null;

  const storedClient = clientList?.find((client) => client.id === clientId) || {};
  const profile = {
    ...storedClient,
    ...data,
    clientId,
    visaStatus:
      blankIfPlaceholder(data?.visaStatus) || blankIfPlaceholder(storedClient?.visaStatus),
    caseOfficer: data?.caseOfficer || storedClient?.caseOfficer,
  };

  const visaExpiry = getExpiryMeta(profile.visaExpiry);
  const passportExpiry = getExpiryMeta(profile.passportExpiry);

  return (
    <Paper variant="outlined" className="border-app-border">
      <Box className="px-3 py-2">
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Stack direction="row" spacing={1.25} alignItems="center" className="min-w-0">
            <Avatar
              sx={{ bgcolor: "#ea580c", width: 32, height: 32, fontSize: 13, fontWeight: 700 }}
            >
              {getInitials(profile.name)}
            </Avatar>
            <Box className="min-w-0">
              <Typography variant="body2" fontWeight={700} className="truncate">
                {profile.name || "—"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                className="block truncate"
                sx={{ fontSize: 11 }}
              >
                {[profile.email, profile.number].filter(Boolean).join(" · ") || "—"}
              </Typography>
            </Box>
          </Stack>
          {onViewClient && (
            <Button
              variant="text"
              size="small"
              startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              onClick={onViewClient}
              sx={{ flexShrink: 0, minWidth: "auto", px: 0.75, py: 0.25, fontSize: 12 }}
            >
              View
            </Button>
          )}
        </Stack>

        <Box className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1.5 mt-2 pt-2 border-t border-app-border">
          <CompactDetail label="Current visa" value={profile.visaStatus || "Not provided"} />
          <CompactDetail label="Visa expiry" value={visaExpiry.display} chip={visaExpiry.chip} />
          <CompactDetail label="Passport" value={profile.passportNumber || "Not provided"} />
          <CompactDetail
            label="Passport expiry"
            value={passportExpiry.display}
            chip={passportExpiry.chip}
          />
          <CompactDetail label="Case officer" value={profile.caseOfficer || "Not assigned"} />
          <CompactDetail
            label="Reference"
            value={profile.reference || profile.referral || "Not provided"}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default ClientSummaryCard;
